import { aed } from "@/lib/format";
import {
  buildReportDocument,
  openReportPrint,
  periodMeta,
  resolveReportLetterhead,
  escapeReportHtml,
} from "@/lib/reportPrintShell";

function cell(value) {
  return value != null && value !== "" ? aed(value) : "";
}

export async function printTipPoolReport(report, options = {}) {
  if (!report) return false;
  const withBranch = { ...report, branch_id: report.branch_id ?? options.branchId, all_branches: options.allBranches };
  const company = await resolveReportLetterhead({ branchId: withBranch.branch_id, allBranches: withBranch.all_branches });

  const rows = (withBranch.rows || [])
    .map(
      (r) => `
      <tr>
        <td>${escapeReportHtml(r.staff_name)}</td>
        <td class="num">${r.bill_count ?? ""}</td>
        <td class="num">${cell(r.net_sales)}</td>
        <td class="num">${cell(r.tip_total)}</td>
        <td class="num">${cell(r.pool_total)}</td>
      </tr>`,
    )
    .join("");

  const t = withBranch.totals || {};
  const html = buildReportDocument({
    title: "Tip & Service Charge Pool",
    company,
    metaHtml: `${periodMeta({
        from_date: withBranch.from_date || withBranch.date_from,
        to_date: withBranch.to_date || withBranch.date_to,
        payment_method: withBranch.payment_method,
      })}
      <div><strong>Bills:</strong> ${t.bill_count ?? ""}</div>
      <div><strong>Pool total:</strong> ${cell(t.pool_total)}</div>`,
    bodyHtml: `<h2>By Staff</h2>
    <table>
      <thead><tr><th>Staff</th><th>Bills</th><th>Net sales</th><th>Tips</th><th>Pool</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`,
  });

  return openReportPrint(html);
}

export async function printBranchComparisonReport(report, options = {}) {
  if (!report) return false;
  const company = await resolveReportLetterhead({ branchId: options.branchId, allBranches: options.allBranches });

  const rows = (report.rows || [])
    .map(
      (r) => `
      <tr>
        <td>${escapeReportHtml(r.branch_name)}</td>
        <td>${escapeReportHtml(r.city || "—")}</td>
        <td class="num">${cell(r.net_sales)}</td>
        <td class="num">${cell(r.round_off)}</td>
        <td class="num">${r.bill_count ?? ""}</td>
        <td class="num">${cell(r.avg_order_value)}</td>
        <td class="num">${r.delivery_percent ?? ""}%</td>
        <td class="num">${r.food_cost_percent != null ? `${r.food_cost_percent}%` : "—"}</td>
        <td class="num">${r.table_turn ?? ""}</td>
      </tr>`,
    )
    .join("");

  const html = buildReportDocument({
    title: "Branch Comparison",
    company,
    metaHtml: periodMeta({
      from_date: report.from_date || report.date_from,
      to_date: report.to_date || report.date_to,
    }),
    bodyHtml: `<h2>Branches</h2>
    <table>
      <thead><tr><th>Branch</th><th>City</th><th>Net sales</th><th>Round off</th><th>Bills</th><th>AOV</th><th>Delivery %</th><th>Food cost %</th><th>Table turn</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`,
  });

  return openReportPrint(html);
}

export async function printSupplierComplianceReport({ compliance, prices, from_date, to_date }, options = {}) {
  const company = await resolveReportLetterhead({ branchId: options.branchId, allBranches: options.allBranches });

  const alertRows = (compliance?.rows || [])
    .map((r) => {
      const issues = (r.issues || []).map((i) => `${i.label}${i.date ? ` (${i.date})` : ""}`).join("; ");
      return `
      <tr>
        <td>${escapeReportHtml(r.supplier_name)} (${escapeReportHtml(r.supplier_code || "")})</td>
        <td>${escapeReportHtml(issues || "—")}</td>
      </tr>`;
    })
    .join("");

  const priceRows = (prices || [])
    .map(
      (r) => `
      <tr>
        <td>${escapeReportHtml(r.recorded_at?.slice?.(0, 10) || "")}</td>
        <td>${escapeReportHtml(r.ingredient_name)}</td>
        <td>${escapeReportHtml(r.supplier_name || "—")}</td>
        <td class="num">${Number(r.unit_cost).toFixed(4)}</td>
      </tr>`,
    )
    .join("");

  const html = buildReportDocument({
    title: "Supplier Compliance & Ingredient Prices",
    company,
    metaHtml: `${periodMeta({ from_date, to_date })}
      <div><strong>Suppliers needing attention:</strong> ${compliance?.alert_count ?? 0}</div>`,
    bodyHtml: `<h2>Compliance Alerts</h2>
    <table>
      <thead><tr><th>Supplier</th><th>Issues</th></tr></thead>
      <tbody>${alertRows || `<tr><td colspan="2">All suppliers look compliant.</td></tr>`}</tbody>
    </table>
    <h2>Ingredient Price History</h2>
    <table>
      <thead><tr><th>Date</th><th>Ingredient</th><th>Supplier</th><th>Unit cost</th></tr></thead>
      <tbody>${priceRows}</tbody>
    </table>`,
  });

  return openReportPrint(html);
}
